import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

/**
 * Live guider RMS performance plot. Takes data via props so it can render
 * inside or outside an ObservatoryProvider.
 *
 * @param {Object[]} guiderHistory - Rolling history of {time, rms, ra_rms, dec_rms}
 * @param {Object} guider - Current equipment_status.guider snapshot for header readout
 */
const GuiderChart = ({ guiderHistory = [], guider = {} }) => {

  return (
    <Card sx={{ height: '100%', backgroundColor: 'rgba(255,255,255,0.03)' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Guide Performance
          </Typography>
          {typeof guider.rms_error === 'number' && (
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: guider.rms_error > 1.5 ? '#ff9800' : '#4caf50' }}>
              {guider.rms_error.toFixed(2)}" RMS
            </Typography>
          )}
        </Box>

        {guiderHistory.length < 2 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: 'rgba(255,255,255,0.3)' }}>
            <Typography variant="caption">Waiting for guider data...</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={guiderHistory} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="time"
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 9 }}
                interval="preserveStartEnd"
                stroke="rgba(255,255,255,0.1)"
              />
              <YAxis
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 9 }}
                stroke="rgba(255,255,255,0.1)"
                domain={[0, 'auto']}
                unit='"'
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e1e1e',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 4,
                  fontSize: '0.7rem',
                }}
                labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
              />
              <ReferenceLine y={1.5} stroke="rgba(255,152,0,0.4)" strokeDasharray="3 3" />
              <Line
                type="monotone"
                dataKey="rms"
                stroke="#90caf9"
                strokeWidth={1.5}
                dot={false}
                name="Total RMS"
              />
              <Line
                type="monotone"
                dataKey="ra_rms"
                stroke="#4caf50"
                strokeWidth={1}
                dot={false}
                name="RA"
                strokeDasharray="4 2"
              />
              <Line
                type="monotone"
                dataKey="dec_rms"
                stroke="#f48fb1"
                strokeWidth={1}
                dot={false}
                name="Dec"
                strokeDasharray="4 2"
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {guider.connected && (
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5, justifyContent: 'center' }}>
            <Typography variant="caption" sx={{ color: '#4caf50', fontSize: '0.6rem' }}>
              RA: {typeof guider.ra_rms === 'number' ? guider.ra_rms.toFixed(2) : '—'}"
            </Typography>
            <Typography variant="caption" sx={{ color: '#f48fb1', fontSize: '0.6rem' }}>
              Dec: {typeof guider.dec_rms === 'number' ? guider.dec_rms.toFixed(2) : '—'}"
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default GuiderChart;
